/**
 * @fileoverview Horizontal bar comparison chart with a real numeric axis.
 * @description Ported from the InsightBox Banking design system's own
 * `charts/HorizontalValueBars.tsx` — gridlines + scale ticks so values can be read off the chart
 * itself, not just from the printed per-bar labels. Render-prop typing uses recharts' own exported
 * `LabelProps` instead of the reference's untyped `any`. Feature-scoped rather than promoted to
 * `shared/` since `HorizontalValueBarItem` is a banking-specific data shape.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { CHART_GRID, CHART_TEXT } from '@/shared/constants/chart-theme.constants'

import type { LabelProps } from 'recharts'

export interface HorizontalValueBarItem {
  key: string
  label: string
  value: number
  displayValue: string
  color: string
}

function makeValueLabel(items: HorizontalValueBarItem[]) {
  return function ValueLabel(props: LabelProps) {
    const { x, y, width, height, index } = props
    const item = index === undefined ? undefined : items[index]
    if (
      !item ||
      typeof x !== 'number' ||
      typeof y !== 'number' ||
      typeof width !== 'number' ||
      typeof height !== 'number'
    ) {
      return null
    }
    return (
      <text
        x={x + width + 8}
        y={y + height / 2 + 4}
        className="text-[12px] font-bold"
        fill={CHART_TEXT}
      >
        {item.displayValue}
      </text>
    )
  }
}

interface HorizontalValueBarsProps {
  items: HorizontalValueBarItem[]
  /** e.g. "₹ Lakhs" — shown on the axis. */
  axisLabel?: string
  labelWidth?: number
  height?: number | '100%'
}

/**
 * @component
 */
function HorizontalValueBars({
  items,
  axisLabel,
  labelWidth = 110,
  height = 200,
}: HorizontalValueBarsProps) {
  const max = Math.max(...items.map((i) => i.value), 1)
  const axisMax = Math.ceil((max * 1.3) / 0.5) * 0.5
  const ValueLabel = makeValueLabel(items)

  return (
    // In fill mode (height="100%"), absolutely fill the nearest positioned ancestor instead of
    // relying on a plain CSS percentage height — see TrendArea.tsx for why. No-op for a fixed
    // numeric height.
    <ResponsiveContainer
      width="100%"
      height={height}
      debounce={200}
      {...(height === '100%' ? { style: { position: 'absolute' as const, inset: 0 } } : {})}
    >
      <BarChart
        data={items}
        layout="vertical"
        margin={{ top: 8, right: 56, left: 0, bottom: axisLabel ? 10 : 4 }}
      >
        <CartesianGrid stroke={CHART_GRID} horizontal={false} />
        <XAxis
          type="number"
          domain={[0, axisMax]}
          axisLine={false}
          tickLine={false}
          tick={{ fill: '#6B7280', fontSize: 11 }}
          {...(axisLabel
            ? {
                label: {
                  value: axisLabel,
                  position: 'insideBottom',
                  offset: -4,
                  fill: '#6B7280',
                  fontSize: 11,
                },
              }
            : {})}
        />
        <YAxis
          type="category"
          dataKey="label"
          interval={0}
          axisLine={false}
          tickLine={false}
          width={labelWidth}
          tick={{ fill: '#111827', fontSize: 13, fontWeight: 600 }}
        />
        <Tooltip
          formatter={(v) => Number(v).toFixed(2)}
          contentStyle={{ borderRadius: 12, borderColor: '#E5E7EB' }}
        />
        <Bar
          dataKey="value"
          radius={[0, 6, 6, 0]}
          barSize={38}
          isAnimationActive={false}
          label={ValueLabel}
        >
          {items.map((item) => (
            <Cell key={item.key} fill={item.color} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

export default HorizontalValueBars
